import * as React from "react";
import {
  Navbar,
  Button,
  ButtonToolbar,
  Form,
  Container,
  Row,
  Col,
  Modal,
} from "react-bootstrap";
import I18n from "lib/I18n";
import { Loader } from "lib/components/Loader";
import { HelpButton } from "./HelpButton";
import { SuggestionDialog } from "../SuggestionDialog";
import style from "./style.module.css";
import { AdvancedOptions } from "./AdvancedOptions";
import { getStore } from "../../store";
import { observer } from "mobx-react-lite";
import {
  BsFillTrashFill,
  BsPlusSquareFill,
  BsDashSquare,
  BsPlusSquare,
} from "react-icons/bs";
import { Entry, EntryInterface } from "bundles/EventMasterEditor/store/Entry";
import { Typeahead } from "react-bootstrap-typeahead";
import { dataTypes } from "bundles/EventMasterEditor/constants";

interface EntryRowProps {
  entry: Entry;
  prefix: string;
  index: number;
  depth: number;
  parent?: Entry;
  onChange<T extends keyof EntryInterface>(
    entry: Entry,
    target: T,
    value: EntryInterface[T],
  ): void;
  onRemove(entry: Entry | undefined, index: number): void;
  onAdd(entry: Entry): void;
}

const EntryRow = observer((props: EntryRowProps) => {
  const { entry, prefix, index, depth, parent, onChange, onRemove, onAdd } =
    props;
  const [expanded, setExpanded] = React.useState(true);

  const keyErrors = entry.error.messages.get("key") ?? [];
  const typeErrors = entry.error.messages.get("type") ?? [];
  const isRecord = entry.type === "RECORD";

  return (
    <div>
      <Row className="mb-2">
        <Col xs="auto" className={style.toggleCol}>
          {isRecord ? (
            <span
              className={style.toggleExpand}
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? <BsDashSquare /> : <BsPlusSquare />}
            </span>
          ) : (
            <span className={style.toggleExpandPlaceholder} />
          )}
        </Col>
        <Col xs={2}>
          <Form.Control
            size="sm"
            type="text"
            placeholder={I18n.t("event_master_editor.label.key")}
            value={entry.key}
            onChange={(e) => onChange(entry, "key", e.currentTarget.value)}
          />
          {keyErrors.length > 0 && (
            <Form.Text>
              <ul>
                {keyErrors.map((error) => (
                  <li className={style[error.level]} key={error.message}>
                    {error.message}
                  </li>
                ))}
              </ul>
            </Form.Text>
          )}
        </Col>
        <Col xs={2}>
          <Typeahead
            id={`${depth}-${index}`}
            key={entry.key}
            size="sm"
            placeholder={I18n.t("event_master_editor.label.type")}
            defaultInputValue={entry.type}
            onInputChange={(e) => onChange(entry, "type", e || "")}
            onChange={(e) => onChange(entry, "type", (e[0] as string) || "")}
            options={dataTypes}
          />
          {typeErrors.length > 0 && (
            <Form.Text>
              <ul>
                {typeErrors.map((error) => (
                  <li className={style[error.level]} key={error.message}>
                    {error.message}
                  </li>
                ))}
              </ul>
            </Form.Text>
          )}
        </Col>
        <Col xs="auto">
          <Form.Check
            className="mt-1"
            type="checkbox"
            id={`${prefix}-repeated`}
            label={I18n.t("event_master_editor.label.repeated")}
            checked={entry.repeated}
            onChange={(e) =>
              onChange(entry, "repeated", e.currentTarget.checked)
            }
          />
        </Col>
        <Col xs={6}>
          <Form.Control
            size="sm"
            as="textarea"
            rows={1}
            className={style.entryListMemoCell}
            placeholder={I18n.t("event_master_editor.label.note")}
            value={entry.note}
            onChange={(e) => onChange(entry, "note", e.currentTarget.value)}
          />
        </Col>
        <Col xs="auto">
          <Button size="sm" onClick={() => onRemove(parent, index)}>
            <BsFillTrashFill />
          </Button>
        </Col>
      </Row>
      {isRecord && expanded ? (
        <>
          {entry.fields.length > 0 && (
            <div className={style.treeChildren}>
              {entry.fields.map((field, i) => (
                <div
                  className={`${style.treeNode} ${field.type === "RECORD" ? style.treeNodeRecord : ""}`}
                  key={i}
                >
                  <EntryRow
                    entry={field}
                    index={i}
                    prefix={`${prefix}-${entry.key}-${i}`}
                    depth={depth + 1}
                    parent={entry}
                    onChange={onChange}
                    onRemove={onRemove}
                    onAdd={onAdd}
                  />
                </div>
              ))}
            </div>
          )}
          <div className={style.treeAddRow}>
            <Row className="mb-2">
              <Col xs="auto" className={style.toggleCol}>
                <span className={style.toggleExpandPlaceholder} />
              </Col>
              <Col xs="auto" style={{ marginLeft: 12 }}>
                <Button size="sm" onClick={() => onAdd(entry)}>
                  <BsPlusSquareFill className="me-2" />
                  {I18n.t("event_master_editor.action.add")}
                </Button>
              </Col>
            </Row>
          </div>
        </>
      ) : undefined}
    </div>
  );
});

export const EntryList = observer(() => {
  const { app, list } = getStore();
  const [showSaveModal, setShowSaveModal] = React.useState(false);

  const handleAddEntry = (entry?: Entry) => {
    list.addEntry(entry);
  };

  const handleReloadDefinition = () => {
    list.reloadDefinition();
  };

  const handleShowSuggestion = () => {
    list.suggestion.isOpened = true;
  };

  const handleShowAdvancedOptions = () => {
    list.isAdvancedOptionsOpen = true;
  };

  const handleOpenSaveModal = () => {
    list.versionNote = "";
    setShowSaveModal(true);
  };

  const handleCloseSaveModal = () => {
    setShowSaveModal(false);
  };

  const handleSubmit = () => {
    setShowSaveModal(false);
    list.postDefinition();
  };

  const renderMain = () => {
    return (
      <div className={style.root}>
        <div className={style.toolbar}>
          <Container fluid>
            <Navbar>
              <div className="me-auto">
                <Form>
                  <ButtonToolbar>
                    <Button
                      className="me-2"
                      variant="light"
                      onClick={() => {
                        handleAddEntry();
                      }}
                    >
                      {I18n.t("event_master_editor.action.add")}
                    </Button>
                    <Button
                      className="me-2"
                      variant="primary"
                      onClick={handleOpenSaveModal}
                      disabled={list.hasError() || list.isSaved}
                    >
                      {I18n.t("event_master_editor.action.save")}
                    </Button>
                    <Button
                      className="me-2"
                      variant="danger"
                      onClick={handleReloadDefinition}
                    >
                      {I18n.t("event_master_editor.action.rollback")}
                    </Button>
                    <Button
                      className="me-2"
                      variant="light"
                      onClick={handleShowSuggestion}
                    >
                      {I18n.t("event_master_editor.action.suggestion")}
                    </Button>
                    <Button
                      className="me-2"
                      variant="light"
                      onClick={handleShowAdvancedOptions}
                    >
                      {I18n.t("event_master_editor.label.advanced_options")}
                    </Button>
                    <HelpButton />
                  </ButtonToolbar>
                </Form>
              </div>
              <div>
                <span className={style.version}>
                  {`Version: ${app.versionNumber ?? ""}`}
                </span>
              </div>
            </Navbar>
          </Container>
        </div>
        <div className={style.entries}>
          {list.entryList.map((entry, i) => (
            <EntryRow
              key={i}
              entry={entry}
              index={i}
              prefix={i.toString()}
              depth={0}
              onChange={(entry, target, value) =>
                list.updateEntry(entry, target, value)
              }
              onRemove={(entry, index) => list.removeEntry(entry, index)}
              onAdd={(entry) => handleAddEntry(entry)}
            />
          ))}
        </div>
        <AdvancedOptions />
        <SuggestionDialog />
        <Modal
          animation={false}
          show={showSaveModal}
          onHide={handleCloseSaveModal}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {I18n.t("event_master_editor.action.save")}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group>
              <Form.Label>
                {I18n.t("event_master_editor.label.version_note")}
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={list.versionNote}
                onChange={(e) => (list.versionNote = e.currentTarget.value)}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseSaveModal}>
              {I18n.t("event_master_editor.action.close")}
            </Button>
            <Button variant="primary" onClick={handleSubmit}>
              {I18n.t("event_master_editor.action.save")}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  };

  return <Loader show={list.isLoading}>{renderMain()}</Loader>;
});
